import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SkillsSection = () => {
  const [activeCategory, setActiveCategory] = useState('frontend');

  const skillCategories = {
    frontend: {
      title: "Frontend Excellence",
      icon: "Layout",
      description: "Building responsive, performant user interfaces that users love",
      skills: [
        { name: "React & Next.js", level: 95, icon: "Code2" },
        { name: "TypeScript", level: 90, icon: "FileCode" },
        { name: "Tailwind CSS", level: 95, icon: "Palette" },
        { name: "State Management", level: 90, icon: "Database" },
        { name: "Performance Optimization", level: 85, icon: "Zap" },
        { name: "Responsive Design", level: 95, icon: "Smartphone" }
      ]
    },
    backend: {
      title: "Backend Architecture",
      icon: "Server",
      description: "Scalable server-side solutions that handle real-world demands",
      skills: [
        { name: "Node.js & Express", level: 90, icon: "Code2" },
        { name: "RESTful APIs", level: 95, icon: "Network" },
        { name: "Database Design", level: 85, icon: "Database" },
        { name: "Authentication & Security", level: 90, icon: "Shield" },
        { name: "Microservices", level: 80, icon: "Boxes" },
        { name: "API Integration", level: 90, icon: "Plug" }
      ]
    },
    devops: {
      title: "DevOps & Deployment",
      icon: "Cloud",
      description: "Automated workflows and reliable production environments",
      skills: [
        { name: "CI/CD Pipelines", level: 85, icon: "GitBranch" },
        { name: "Docker & Containers", level: 80, icon: "Package" },
        { name: "Cloud Platforms", level: 85, icon: "Cloud" },
        { name: "Monitoring & Logging", level: 80, icon: "Activity" },
        { name: "Performance Tuning", level: 85, icon: "Gauge" },
        { name: "Version Control", level: 95, icon: "GitCommit" }
      ]
    },
    business: {
      title: "Business Impact",
      icon: "TrendingUp",
      description: "Technical skills that drive measurable business outcomes",
      skills: [
        { name: "Technical Strategy", level: 90, icon: "Target" },
        { name: "Client Communication", level: 95, icon: "MessageSquare" },
        { name: "Project Management", level: 85, icon: "CheckSquare" },
        { name: "Requirements Analysis", level: 90, icon: "FileSearch" },
        { name: "Cost Optimization", level: 85, icon: "DollarSign" },
        { name: "Team Collaboration", level: 90, icon: "Users" }
      ]
    }
  };

  const categories = Object.keys(skillCategories);

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-card">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Icon name="Wrench" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">Technical Expertise</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-foreground mb-4 md:mb-6">
            Skills That Drive Results
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            My technical toolkit is built around one goal: delivering production-ready solutions that solve real business problems and create measurable value.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 md:gap-4 mb-8 md:mb-12">
          {categories?.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`
                flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 rounded-lg font-medium transition-all duration-300
                ${activeCategory === category
                  ? 'bg-primary text-primary-foreground shadow-lg'
                  : 'bg-background text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              <Icon name={skillCategories?.[category]?.icon} size={18} />
              <span className="text-sm md:text-base">{skillCategories?.[category]?.title}</span>
            </button>
          ))}
        </div>

        <div className="bg-background rounded-2xl p-6 md:p-8 lg:p-12 border border-border">
          <div className="mb-8">
            <h3 className="text-2xl md:text-3xl font-bold font-heading text-foreground mb-2">
              {skillCategories?.[activeCategory]?.title}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground">
              {skillCategories?.[activeCategory]?.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {skillCategories?.[activeCategory]?.skills?.map((skill, index) => (
              <div key={index} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name={skill?.icon} size={18} className="text-primary md:w-5 md:h-5" />
                    </div>
                    <span className="text-sm md:text-base font-medium text-foreground">{skill?.name}</span>
                  </div>
                  <span className="text-sm font-bold text-primary">{skill?.level}%</span>
                </div>
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${skill?.level}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          <div className="bg-background rounded-xl p-6 md:p-8 border border-border text-center">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Code2" size={24} className="text-primary md:w-8 md:h-8" />
            </div>
            <div className="text-3xl md:text-4xl font-bold text-foreground mb-2">15+</div>
            <div className="text-sm md:text-base text-muted-foreground">Technologies Mastered</div>
          </div>
          <div className="bg-background rounded-xl p-6 md:p-8 border border-border text-center">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="Award" size={24} className="text-primary md:w-8 md:h-8" />
            </div>
            <div className="text-3xl md:text-4xl font-bold text-foreground mb-2">8+</div>
            <div className="text-sm md:text-base text-muted-foreground">Professional Certifications</div>
          </div>
          <div className="bg-background rounded-xl p-6 md:p-8 border border-border text-center">
            <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="BookOpen" size={24} className="text-primary md:w-8 md:h-8" />
            </div>
            <div className="text-3xl md:text-4xl font-bold text-foreground mb-2">100+</div>
            <div className="text-sm md:text-base text-muted-foreground">Hours Learning Monthly</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;